/**
 * MarketplacePage
 *
 * WP-SL-V3: passes userRoles to useCatalog for visibility filtering,
 * and feeds loaded tiles (not static config tiles) to CategorySidebar
 * so categories reflect the actual API-sourced catalogue.
 *
 * catalogue-card-v2: content column gap tightened to `--sp-4` to match
 * the card grid's own gap token; no logic change.
 *
 * brief-13-shell-nav-rail: CategorySidebar is now a horizontal tab row
 * above the grid instead of a left-hand column, so the page is a single
 * full-width column rather than a two-column flex split.
 */

import { useMemo, useState } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { useCatalog, type CatalogState } from '../hooks/useCatalog';
import { t } from '../i18n';
import CategorySidebar from '../components/CategorySidebar';
import ViewToggle from '../components/ViewToggle';
import { sortPinnedFirst } from '../hooks/useCatalogPrefs';
import { usePrefs } from '../prefs/PrefsContext';
import TileGrid, { TileGridSkeleton } from '../components/TileGrid';
import EmptyState from '../components/EmptyState';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import { isAuthenticatedBroker, META_MODE } from '../config/schema';
import type { CartItem } from './CartPage';

interface MarketplacePageProps {
  previewState?: CatalogState['tag'];
  onAddToCart?: (item: CartItem) => void;
  onNavigate?: (path: Route) => void;
}

export default function MarketplacePage({ previewState, onAddToCart, onNavigate }: MarketplacePageProps) {
  const config = useConfig();
  const { token, user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  // Any authenticated broker (meta or tenant) reads its catalogue from the API
  // and binds through POST /bindings.
  const isApiCatalog = isAuthenticatedBroker(config.instance.mode);
  // brief-17-admin-catalogue-tile-actions: only the META broker's catalogue is the
  // admin's own service catalogue (graph: decision-2026-05-30-admin-broker-functions,
  // service catalogue management). A tenant broker's catalogue is a consumer surface:
  // no admin actions, and the storefront card treatments still apply there
  // (convention-admin-catalogue-no-commerce-cta).
  const isAdminCatalog = config.instance.mode === META_MODE || config.instance.mode === 'tier-1-hub';
  // Stable identity: a fresh [] every render re-triggers useCatalog's effect
  // (userRoles is in its deps) and permanently restarts the skeleton timer
  // when auth is disabled and `user` is null.
  const userRoles = useMemo(() => user?.roles ?? [], [user]);

  // brief-26: pins and view mode are per browser, per instance, per user
  // (see useCatalogPrefs for why that scope and what it costs).
  const { prefs, togglePin, setView } = usePrefs();

  const { state, retry } = useCatalog({
    tiles: config.catalog.tiles,
    activeCategory,
    search,
    mode: config.instance.mode,
    token,
    userRoles,
    preview: previewState,
  });

  // In vertical-ref mode, use loaded tiles for sidebar so tags are API-accurate.
  // Fall back to config tiles (empty in vertical-ref, used by other modes).
  const sidebarTiles: TileConfig[] = isApiCatalog && state.tag === 'loaded'
    ? state.tiles
    : config.catalog.tiles;

  function clearFilters() {
    setActiveCategory('All');
    setSearch('');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', width: '100%' }}>
      <CategorySidebar
        tiles={sidebarTiles}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', maxWidth: '1200px' }}>
        {/* Search + eyebrow */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <p className="dp-eyebrow">{t('marketplace.eyebrow')}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            {isAdminCatalog && <ViewToggle view={prefs.view} onChange={setView} />}
            <input
            type="search"
            placeholder={t('marketplace.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: '6px var(--sp-3)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
              width: '220px',
            }}
            />
          </div>
        </div>

        {/* Data state machine */}
        {state.tag === 'loading' && <TileGridSkeleton count={config.catalog.tiles.length || 6} />}

        {state.tag === 'loaded' && (
          <TileGrid
            tiles={isAdminCatalog ? sortPinnedFirst(state.tiles, prefs.pinned) : state.tiles}
            useBindingFlow={isApiCatalog}
            onAddToCart={onAddToCart}
            isAdminCatalog={isAdminCatalog}
            onNavigate={onNavigate}
            view={prefs.view}
            pinnedIds={prefs.pinned}
            onTogglePin={togglePin}
          />
        )}

        {state.tag === 'empty-no-results' && (
          <EmptyState variant="no-results" onAction={clearFilters} />
        )}

        {state.tag === 'empty-no-access' && (
          <EmptyState variant="no-access" />
        )}

        {state.tag === 'empty-fetch-failed' && (
          <EmptyState
            variant="fetch-failed"
            errorCode={state.errorCode}
            onAction={retry}
          />
        )}
      </div>
    </div>
  );
}
